import React, {
    CSSProperties,
    MouseEvent,
    useEffect,
    useRef,
    useState
} from 'react';
import { HBTabsProps, TabType } from './HBTabs';
import { Tab, TabTitle, tabTitleStyle } from './HBTabs.styles';
import { DotsVertical12 } from '@honeybook/icons';
import HBFlex from '../HBFlex/HBFlex';
import HBMenu from '../HBMenu/HBMenu';
import HBIconButton from '../HBIconButton/HBIconButton';
import { useEllipsis } from '../../hooks/useEllipsis';
import HBTooltip from '../HBTooltip/HBTooltip';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';

interface Props {
    hasMenu: boolean;
    isActive: boolean;
    isIconOnly: boolean;
    maxTabWidth?: HBTabsProps['maxTabWidth'];
    onClick: HBTabsProps['onClick'];
    renderTab: HBTabsProps['renderTab'];
    scrollRef: React.MutableRefObject<HTMLElement | null>;
    selectedHighlightColor: HBTabsProps['selectedHighlightColor'];
    size: HBTabsProps['size'];
    stretchToFill: HBTabsProps['stretchToFill'];
    tab: TabType;
}

const TabItem = ({
    hasMenu,
    isActive,
    isIconOnly,
    maxTabWidth,
    onClick,
    renderTab,
    scrollRef,
    selectedHighlightColor,
    size,
    stretchToFill,
    tab
}: Props) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const tabContainerRef = useRef<HTMLDivElement>(null);
    const [tabTextRef, isOverflowing] = useEllipsis({ textValue: tab.title });

    const textType = tabTitleStyle[size][isActive ? 'active' : 'notActive'];

    const tabTestId = useBuildTestIdInternal({
        identifier: tab.testId || tab.id,
        suffix: 'tab',
        componentName: 'HBTabs'
    });

    useEffect(() => {
        const scrollContainer = scrollRef.current;
        const tabContainer = tabContainerRef.current;
        if (!isActive || !scrollContainer || !tabContainer) {
            return;
        }
        const scrollLeft =
            tabContainer.offsetLeft -
            (scrollContainer.clientWidth - tabContainer.clientWidth) / 2;
        scrollContainer.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }, [isActive, tab.title, tabTextRef, tab, scrollRef]);

    const handleClick = (ev?: MouseEvent<HTMLElement>) => {
        ev?.currentTarget?.blur();
        if (!tab.isDisabled && onClick) {
            onClick(tab.id);
        }
    };

    if (renderTab) {
        return renderTab({
            ...tab,
            isActive,
            onClick: handleClick
        });
    }

    // Base props common to both link and button tabs
    const tabInteractionProps: React.ComponentProps<typeof Tab> & {
        to?: string;
    } = {
        onClick: handleClick
    };

    if (tab.to) {
        tabInteractionProps.to = tab.to;
        tabInteractionProps['aria-current'] = isActive ? 'page' : undefined;
    }

    return (
        <Tab
            {...tabInteractionProps}
            role="tab"
            aria-selected={isActive}
            data-is-active={isActive}
            $hasMenu={hasMenu}
            $isActive={isActive}
            isDisabled={tab.isDisabled}
            $isIconOnly={isIconOnly}
            $isMenuOpen={isMenuOpen}
            key={tab.id}
            $maxTabWidth={maxTabWidth}
            ref={tabContainerRef}
            $selectedHighlightColor={selectedHighlightColor}
            $stretchToFill={stretchToFill}
            testId={tabTestId}
        >
            {tab.icon}
            {tab.title && (
                <HBTooltip
                    disabled={!isOverflowing}
                    offsetY={4}
                    placement="top"
                    target={
                        <TabTitle ref={tabTextRef} type={textType}>
                            {tab.title}
                        </TabTitle>
                    }
                    targetContainerStyle={tooltipTargetContainerStyle}
                >
                    {tab.title}
                </HBTooltip>
            )}
            {tab.suffix && (
                <HBFlex
                    displayName="TabSuffixContainer"
                    align="center"
                    marginLeft="4"
                >
                    {tab.suffix}
                </HBFlex>
            )}
            {tab.menuItems && (
                <HBMenu
                    align="start"
                    menuItems={tab.menuItems}
                    onOpenChange={setIsMenuOpen}
                    preventClickPropogation
                    size="md"
                    target={
                        <HBIconButton
                            buttonTheme="subtle-invisible"
                            icon={DotsVertical12}
                            isSelected={isMenuOpen}
                            label="tab-menu"
                            size="xxs"
                            testId="tab-menu-item"
                        />
                    }
                    targetAsChild
                    targetContainerStyle={menuTargetContainerStyle}
                    testIdName="tab-menu"
                    withArrow={false}
                />
            )}
        </Tab>
    );
};

export default TabItem;

// -------------------------------- Styles -------------------------------- //
const tooltipTargetContainerStyle: CSSProperties = {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
};

const menuTargetContainerStyle: CSSProperties = {
    transition: 'all 0.15s ease-in',
    position: 'absolute',
    opacity: 0,
    transform: 'translate(-50%, -50%)',
    marginLeft: '4px'
};
